// Recursive function to sort components by position
export function sortComponentsByPosition(components) {
    return [...components]
        .sort((a, b) => a.position - b.position)
        .map((component) => (Object.assign(Object.assign({}, component), { children: component.children ? sortComponentsByPosition(component.children) : [] })));
}
// Helper function to apply optimistic component updates
export function applyOptimisticComponentUpdate(components, componentId, updateData) {
    function updateComponentRecursively(componentList) {
        return componentList.map((component) => {
            if (component.id === componentId) {
                // Apply the updates to the matching component
                return Object.assign(Object.assign({}, component), updateData);
            }
            // Recursively check children
            if (component.children && component.children.length > 0) {
                return Object.assign(Object.assign({}, component), { children: updateComponentRecursively(component.children) });
            }
            return component;
        });
    }
    return updateComponentRecursively(components);
}
// Helper function to apply position updates optimistically
export function applyOptimisticReorderUpdates(components, positionUpdates) {
    // Build a lookup map for position updates to avoid repeated searches
    const updatesById = new Map();
    positionUpdates.forEach((update) => {
        updatesById.set(Number(update.id), update);
    });
    // Flatten the existing tree structure into a simple array
    const flatComponents = [];
    function flattenTree(nodes) {
        nodes.forEach((node) => {
            flatComponents.push(node);
            if (node.children && node.children.length > 0) {
                flattenTree(node.children);
            }
        });
    }
    flattenTree(components);
    // Create a new flat array with updates applied immutably
    const idToNode = new Map();
    const updatedFlatComponents = flatComponents.map((component) => {
        const update = updatesById.get(Number(component.id));
        const updated = Object.assign(Object.assign({}, component), { 
            // Children will be rebuilt from the flat structure
            children: [] });
        if (update) {
            // Only update position if it's provided (not undefined)
            if (Object.prototype.hasOwnProperty.call(update, 'position')) {
                updated.position = update.position;
            }
            // Only update parentId if the property exists in the update object
            // This preserves existing parentId values when the property is intentionally omitted
            // (e.g., for children when only updating attachableId)
            if (Object.prototype.hasOwnProperty.call(update, 'parentId') && update.parentId !== undefined) {
                updated.parentId = update.parentId;
            }
            // Handle attachable changes (only overwrite when a concrete value is provided)
            if (Object.prototype.hasOwnProperty.call(update, 'attachableId') && update.attachableId !== undefined) {
                updated.attachableId = update.attachableId;
            }
            // Handle attachable type changes
            if (Object.prototype.hasOwnProperty.call(update, 'attachableType') && update.attachableType !== undefined) {
                updated.attachableType = update.attachableType;
            }
        }
        idToNode.set(Number(updated.id), updated);
        return updated;
    });
    // Sort the flat list by position so parents and children are ordered before rebuilding the tree
    updatedFlatComponents.sort((a, b) => a.position - b.position);
    // Rebuild the tree structure based on parentId, treating null as root and preserving undefined
    const rootComponents = [];
    updatedFlatComponents.forEach((node) => {
        const { parentId } = node;
        // When parentId is null or undefined, the node is considered a root-level component
        if (parentId === null || parentId === undefined) {
            rootComponents.push(node);
            return;
        }
        const parent = idToNode.get(Number(parentId));
        if (parent) {
            if (!parent.children) {
                parent.children = [];
            }
            parent.children.push(node);
        }
        else {
            // If the parent cannot be found, fall back to treating the node as a root
            rootComponents.push(node);
        }
    });
    // Apply sorting after position updates to maintain correct order at every level
    return sortComponentsByPosition(rootComponents);
}
